'use client'

import * as React from 'react'
import Image from 'next/image'
import * as AvatarPrimitive from '@radix-ui/react-avatar'

import { cn } from '@/lib/utils'
import { getAvatarUrl, type AvatarSource } from '@/lib/avatar/read'

/**
 * Alumnia UI 1.2.1 sizes (docs/redesign/sistema/Avatar.md), in rem so they
 * follow the text size: xs 28, sm 36, md 44 (default), lg 64, xl 96, hero 136,
 * and `file`, the header of a person's file in the admin (ALU-271): 96 on the
 * phone and 120 from 768 px, one element so the photo loads once.
 * The initials are 38 % of the side.
 */
const ENCUENTRO_AVATAR_SIZES = {
  xs: 'size-7 text-[0.665rem]',
  sm: 'size-9 text-[0.855rem]',
  md: 'size-11 text-[1.045rem]',
  lg: 'size-16 text-[1.52rem]',
  xl: 'size-24 text-[2.28rem]',
  hero: 'size-34 text-[3.23rem]',
  file: 'size-24 text-[2.28rem] md:size-30 md:text-[2.85rem]',
} as const

export type EncuentroAvatarSize = keyof typeof ENCUENTRO_AVATAR_SIZES

type AvatarProps = React.ComponentProps<typeof AvatarPrimitive.Root> &
  (
    | { look?: 'classic'; size?: never }
    | {
        /**
         * Encuentro avatar: round, photo first, initials on a stable warm tone
         * otherwise. Decorative (`aria-hidden`): the name is written next to it.
         */
        look: 'encuentro'
        size?: EncuentroAvatarSize
      }
  )

function Avatar({ className, look, size, ...props }: AvatarProps) {
  if (look === 'encuentro') {
    return (
      <AvatarPrimitive.Root
        data-slot="avatar"
        data-look="encuentro"
        aria-hidden
        className={cn(
          'relative isolate inline-grid shrink-0 place-items-center overflow-hidden rounded-en-full font-en-sans leading-none font-semibold text-en-ink',
          ENCUENTRO_AVATAR_SIZES[size ?? 'md'],
          className
        )}
        {...props}
      />
    )
  }
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        // isolate so AvatarImage's z-10 cannot paint over sibling status/admin pips
        'relative isolate flex size-8 shrink-0 overflow-hidden rounded-full',
        className
      )}
      {...props}
    />
  )
}

type AvatarImageProps = Omit<
  React.ComponentProps<typeof Image>,
  'src' | 'alt' | 'width' | 'height' | 'fill'
> & {
  src?: string | null
  alt?: string
  /** Fires when the image fails so parents can show initials only after error. */
  onImageError?: () => void
  /**
   * Pixel hint for the Next.js Image optimizer. Default 96 covers up to
   * `size-12` avatars at DPR=2. Override for larger placements (profile
   * headers at `size-20`/`size-24` should pass 192+).
   */
  size?: number
  /**
   * Optional variant source. When passed, the component picks the closest
   * pre-baked WebP variant (64/96/256) by `displayCssPx` and serves it
   * with `unoptimized` — bypassing Next.js's optimizer entirely, since the
   * variant is already pre-sized at upload time. Falls back to `src` when
   * the variant lookup misses (corrupt jsonb, missing key, unbackfilled row).
   */
  source?: AvatarSource | null
  /** CSS pixel size of the rendered slot. Picks variant: ≤24→64, ≤48→96, else 256. */
  displayCssPx?: number
}

// Next.js Image optimizer rejects SVGs unless `dangerouslyAllowSVG` is
// enabled (SVGs can embed scripts / external refs). A lot of our org logos
// and seeded avatars are SVG (DiceBear `api.dicebear.com/.../svg`, direct
// `.svg` uploads, etc.), so we bypass the optimizer for those URLs and let
// the browser render the SVG natively via a plain `<img>` — `unoptimized`
// on `next/image` does exactly that. Raster formats (jpg/png/webp/avif from
// Supabase, LinkedIn CDN, user uploads) keep full optimization.
function isLikelySvgUrl(src: string): boolean {
  return /\.svg($|\?)|\/svg($|\?)|[?&]format=svg\b/i.test(src)
}

function isLinkedinCdnUrl(src: string): boolean {
  try {
    const host = new URL(src).host
    return host === 'licdn.com' || host.endsWith('.licdn.com')
  } catch {
    return false
  }
}

/**
 * A Supabase storage object: a hosted project (`*.supabase.co/storage/…`) or
 * the local stack (`http://127.0.0.1:54321/storage/v1/object/public/…`),
 * which the optimizer refuses as a private address. Both are served as they are.
 */
function isSupabaseStorageUrl(src: string): boolean {
  return /supabase\.co\/storage\/|\/storage\/v1\/object\/public\//i.test(src)
}

// We bypass Radix's `AvatarPrimitive.Image` on purpose. Radix preloads the
// raw `src` via a detached `<img>` before rendering, which would double-fetch
// when paired with `next/image` (the detached preload hits the raw Supabase
// URL, `next/image` hits the `/_next/image` optimizer URL — different URLs,
// no shared cache). Instead we render `next/image` absolutely positioned over
// the `AvatarFallback` sibling:
//   - while the image is loading, the browser renders transparent pixels for
//     the <img> box, so the fallback (initials) behind it shows through
//   - once the image decodes, it paints over the fallback
//   - on error, `onError` fires and we unmount, leaving the fallback visible
//
// This is the same pattern LinkedIn / Facebook use on their avatars. We do
// NOT gate on an opacity transition driven by `onLoad` because Suspense
// fallbacks (our chromed skeleton) may be swapped out before hydration
// attaches the load listener, leaving the image stuck invisible. Native
// `<img>` rendering already handles the visual correctly as long as the URL
// doesn't 400 — which is why we detect SVG and pass `unoptimized`.
function AvatarImage({
  className,
  src,
  alt,
  size = 96,
  source,
  displayCssPx,
  onImageError,
  ...props
}: AvatarImageProps) {
  // Variant resolution: source takes precedence; falls back to src on miss.
  const variantUrl = source ? getAvatarUrl(source, displayCssPx ?? size) : null
  const resolvedSrc = variantUrl ?? src ?? null
  // Pre-baked variants are already the right size at the right format —
  // skip Next.js optimization to save a Vercel transformation quota count.
  const usingVariant = variantUrl !== null

  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    setFailed(false)
  }, [resolvedSrc])

  if (!resolvedSrc || failed) return null

  const unoptimized =
    usingVariant ||
    isLikelySvgUrl(resolvedSrc) ||
    isLinkedinCdnUrl(resolvedSrc) ||
    isSupabaseStorageUrl(resolvedSrc)

  return (
    <Image
      data-slot="avatar-image"
      src={resolvedSrc}
      alt={alt ?? ''}
      width={size}
      height={size}
      loading="lazy"
      unoptimized={unoptimized}
      className={cn('absolute inset-0 z-10 aspect-square size-full object-cover', className)}
      onError={() => {
        setFailed(true)
        onImageError?.()
      }}
      {...props}
    />
  )
}

/**
 * Up to two initials, uppercased: "Sofía Castro" → "SC". Each word gives its
 * first letter or digit, by code point (`Array.from`), so a name that starts
 * with an emoji ("🎓 Juan Pérez" → "JP") never splits a surrogate pair into a
 * broken character. A name with no letters at all shows its first symbol.
 */
export function avatarInitials(name: string): string {
  const initials = name
    .split(/\s+/)
    .map((part) => Array.from(part).find((ch) => /[\p{L}\p{N}]/u.test(ch)))
    .filter((ch): ch is string => Boolean(ch))
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return initials || (Array.from(name.trim())[0] ?? '')
}

/** Stable tone 0-3 for a name (the artifact's `tone()`): the same person always gets the same color. */
export function avatarTone(name: string): 0 | 1 | 2 | 3 {
  let n = 0
  for (let i = 0; i < name.length; i++) n = (n + name.charCodeAt(i)) % 4
  return n as 0 | 1 | 2 | 3
}

const ENCUENTRO_AVATAR_TONES = [
  'bg-en-brand-soft',
  'bg-en-highlight',
  'bg-en-warning-soft',
  'bg-en-surface-sunken',
] as const

type AvatarFallbackProps = React.ComponentProps<typeof AvatarPrimitive.Fallback> &
  (
    | { look?: 'classic'; name?: never }
    | {
        look: 'encuentro'
        /** The person's name: gives the initials (unless children are passed) and the tone. */
        name: string
      }
  )

function AvatarFallback({ className, look, name, children, ...props }: AvatarFallbackProps) {
  if (look === 'encuentro') {
    return (
      <AvatarPrimitive.Fallback
        data-slot="avatar-fallback"
        data-tone={avatarTone(name)}
        className={cn(
          'flex size-full items-center justify-center rounded-en-full text-en-ink',
          ENCUENTRO_AVATAR_TONES[avatarTone(name)],
          className
        )}
        {...props}
      >
        {children ?? avatarInitials(name)}
      </AvatarPrimitive.Fallback>
    )
  }
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn('bg-muted flex size-full items-center justify-center rounded-full', className)}
      {...props}
    >
      {children}
    </AvatarPrimitive.Fallback>
  )
}

export { Avatar, AvatarImage, AvatarFallback }
